import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";

export interface UserState {
    state: "unknown" | "in" | "off";
    firebase?: FirebaseUserInfo
    token?: string;
    messageToken?: string;
    priza: {
        state: LoadStateInfo;
        info?: UserInfo;
    }
}

const initialState: UserState = {
    state: "unknown",
    priza: {
        state: notStarted(),
    }
};

export interface RetrieveUserRequest {
}

export const retrieveUser = createAsyncThunk(
    "retrieveUser",
    async (params: RetrieveUserRequest, thunkAPI) => {
        console.log("retrieve user");

        const state = thunkAPI.getState() as RootState;
        const token = state.user.token;
        if (!token) {
            return thunkAPI.rejectWithValue({
                status: 401,
                message: "Unauthorized",
                error: "No token available"
            });
        }

        const response = await fetch(`${getBackendUrl()}/api/user`, {
            signal: thunkAPI.signal,
            credentials: "include",
            headers: {
                "Authorization": `Bearer ${token}`,
                "X-Message-Token": state.user.messageToken || "",
                "X-Flavor-Token": "school",
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export interface LogoutUserRequest {
}

export const logoutUser = createAsyncThunk(
    "logoutUser",
    async (params: LogoutUserRequest, thunkAPI) => {
        console.log("logout user");

        const response = await fetch(`${getBackendUrl()}/api/user/logout`, {
            signal: thunkAPI.signal,
            credentials: "include",
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${getToken()}`
            }
        });

        if (response.ok) {
            return "OK";
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export const userSlice = createSlice({
    name: "user",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        setFirebaseUser: (state, action: PayloadAction<FirebaseUserInfo>) => {
            state.firebase = action.payload;
        },
        ready: (state, action: PayloadAction<FirebaseUserInfo>) => {
            state.firebase = action.payload;
            state.state = "in";
            state.priza.state = notStarted();
        },
        notReady: (state) => {
            console.log("not ready");
            state.firebase = undefined;
            state.state = "off";
            state.token = undefined;
            state.messageToken = undefined;
            state.priza.info = undefined;
            state.priza.state = notStarted();
        },
        setToken: (state, action: PayloadAction<string>) => {
            if ((!state.token) || (!action.payload) || state.token != action.payload) {
                state.priza.state = notStarted();
            }
            state.token = action.payload;
        },
        setMessageToken: (state, action: PayloadAction<string>) => {
            state.messageToken = action.payload;
        },
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveUser.pending, (state, action) => {
                state.priza.state = loading();
            })
            .addCase(retrieveUser.fulfilled, (state, action) => {
                console.debug(`${action.type} :: ${JSON.stringify(action.payload)}`);
                state.priza.state = loaded();
                state.priza.info = action.payload;
            })
            .addCase(retrieveUser.rejected, (state, action) => {
                state.priza.state = error(action);
            })
            .addCase(logoutUser.pending, (state, action) => {
                state.priza.state = loading();
            })
            .addCase(logoutUser.fulfilled, (state, action) => {
                console.debug(`${action.type} :: ${JSON.stringify(action.payload)}`);
                state.priza.state = notStarted();
                state.priza.info = undefined;
            })
            .addCase(logoutUser.rejected, (state, action) => {
                state.priza.state = error(action);
            });
    },
});

export interface FirebaseUserInfo {
    displayName: string | null;
    email: string | null;
    phoneNumber: string | null;
    photoURL: string | null;
    providerId: string | null;
    uid: string | null;
    emailVerified: boolean;
    isAnonymous: boolean;
    refreshToken: string | null;
    tenantId: string | null;
}

export interface UserInfo {
    id: string;
    uid: string;
    name: string;
    email: string;
    url?: string;

    permissions: Record<string, string[]>;
}

export const { } = userSlice.actions;

export const selectUser = (state: RootState) => state.user;

export default userSlice.reducer;

// onAuthStateChanged(auth, (newUser) => {
//     console.log("onAuthStateChanged", newUser);
//     if (newUser) {
//         console.log("set new user");
//         const info: FirebaseUserInfo = {
//             displayName: newUser.displayName,
//             email: newUser.email,
//             phoneNumber: newUser.phoneNumber,
//             photoURL: assertThumbnailUrl(newUser.photoURL),
//             providerId: newUser.providerId,
//             uid: newUser.uid,
//             emailVerified: newUser.emailVerified,
//             isAnonymous: newUser.isAnonymous,
//             refreshToken: newUser.refreshToken,
//             tenantId: newUser.tenantId,
//         };
//         store.dispatch( setFirebaseUser(info) );
//     } else {
//       store.dispatch( userSlice.actions.notReady() );
//     }
// });